/**
 * Layout panel — controls metadata + display flags for the indicator
 * (title, description, target value, SMA, show labels/totals).
 *
 * The visibility of certain controls depends on the chart type
 * (e.g. target / SMA only apply to bar/line/kpi).
 */

import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Checkbox } from '../../../components/ui/checkbox';
import { type IndicatorBuilderConfig } from '../../../lib/api/reporting';
import { type BuilderChartType } from './ChartTypePicker';

interface LayoutPanelProps {
  /** Current builder config snapshot. */
  config: IndicatorBuilderConfig;
  onChange: (next: IndicatorBuilderConfig) => void;
  chartType: BuilderChartType;
  /** Title shown above the chart (mirrors `DashboardIndicator.name`). */
  title: string;
  onTitleChange: (next: string) => void;
}

/** Chart types that benefit from a target reference value. */
const SUPPORTS_TARGET: BuilderChartType[] = ['bar', 'line', 'kpi'];
const SUPPORTS_SMA: BuilderChartType[] = ['bar', 'line'];

export function LayoutPanel({
  config,
  onChange,
  chartType,
  title,
  onTitleChange,
}: LayoutPanelProps) {
  const showTarget = SUPPORTS_TARGET.includes(chartType);
  const showSma = SUPPORTS_SMA.includes(chartType);

  const description = typeof config.description === 'string' ? config.description : '';
  const target = config.target_value ?? '';
  const showSmaFlag = Boolean(config.show_sma);
  const smaPeriod = config.sma_period ?? 3;
  const showLabel = Boolean(config.show_label);
  const showTotal = Boolean(config.show_total);

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label className="text-xs">Title</Label>
        <Input
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder="Indicator title"
          className="h-8 text-xs"
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Description</Label>
        <Textarea
          value={description}
          onChange={(e) => onChange({ ...config, description: e.target.value })}
          placeholder="Optional description shown below the title"
          rows={2}
          className="text-xs"
        />
      </div>
      {showTarget && (
        <div className="space-y-1">
          <Label className="text-xs">Target value</Label>
          <Input
            type="number"
            value={target === null ? '' : target}
            onChange={(e) => {
              const raw = e.target.value;
              const parsed = raw === '' ? null : parseFloat(raw);
              onChange({ ...config, target_value: Number.isNaN(parsed) ? null : parsed });
            }}
            placeholder="e.g. 70"
            className="h-8 text-xs"
          />
        </div>
      )}
      {showSma && (
        <div className="grid grid-cols-2 gap-2 items-end">
          <label className="flex items-center gap-2 text-xs">
            <Checkbox
              checked={showSmaFlag}
              onCheckedChange={(checked) =>
                onChange({ ...config, show_sma: Boolean(checked) })
              }
            />
            <span>Show SMA</span>
          </label>
          <div className="space-y-1">
            <Label className="text-xs">SMA period</Label>
            <Input
              type="number"
              min={2}
              max={36}
              value={smaPeriod === null ? '' : smaPeriod}
              onChange={(e) => {
                const parsed = parseInt(e.target.value, 10);
                onChange({
                  ...config,
                  sma_period: Number.isNaN(parsed) ? null : parsed,
                });
              }}
              className="h-8 text-xs"
              disabled={!showSmaFlag}
            />
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <label className="flex items-center gap-2 text-xs">
          <Checkbox
            checked={showLabel}
            onCheckedChange={(checked) => onChange({ ...config, show_label: Boolean(checked) })}
          />
          <span>Show data labels</span>
        </label>
        <label className="flex items-center gap-2 text-xs">
          <Checkbox
            checked={showTotal}
            onCheckedChange={(checked) => onChange({ ...config, show_total: Boolean(checked) })}
          />
          <span>Show total row</span>
        </label>
      </div>
    </div>
  );
}
